import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface ProtocolStep {
  step: number;
  instruction: string;
  warning?: string;
}

interface EmergencyProtocolCardProps {
  title: string;
  category: string;
  severity: 'critical' | 'high' | 'moderate';
  description: string;
  steps: ProtocolStep[];
  whenToSeekHelp: string[];
}

const EmergencyProtocolCard: React.FC<EmergencyProtocolCardProps> = ({
  title,
  category,
  severity,
  description,
  steps,
  whenToSeekHelp
}) => {
  const severityColors = {
    critical: 'bg-error text-white',
    high: 'bg-warning text-foreground',
    moderate: 'bg-primary text-white'
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                {category}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${severityColors[severity]}`}>
                {severity.toUpperCase()}
              </span>
            </div>
            <h3 className="text-2xl font-headline font-bold text-foreground">{title}</h3>
          </div>
          <Icon name="DocumentTextIcon" size={32} variant="outline" className="text-primary flex-shrink-0" />
        </div>

        <p className="text-muted-foreground mb-6">{description}</p>

        <div className="mb-6">
          <h4 className="text-lg font-headline font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="ClipboardDocumentCheckIcon" size={20} variant="solid" className="text-primary" />
            <span>Emergency Steps</span>
          </h4>
          <div className="space-y-4">
            {steps.map((step) => (
              <div key={step.step} className="flex space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold">
                  {step.step}
                </div>
                <div className="flex-1">
                  <p className="text-foreground font-medium">{step.instruction}</p>
                  {step.warning && (
                    <div className="mt-2 flex items-start space-x-2 bg-warning/10 border border-warning rounded-lg p-3">
                      <Icon name="ExclamationTriangleIcon" size={16} variant="solid" className="text-warning flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-foreground">{step.warning}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-error/10 border border-error rounded-lg p-4">
          <h4 className="text-lg font-headline font-semibold text-error mb-3 flex items-center space-x-2">
            <Icon name="PhoneIcon" size={20} variant="solid" />
            <span>Seek Immediate Veterinary Care If:</span>
          </h4>
          <ul className="space-y-2">
            {whenToSeekHelp.map((condition, index) => (
              <li key={index} className="flex items-start space-x-2">
                <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-error flex-shrink-0 mt-1" />
                <span className="text-foreground">{condition}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EmergencyProtocolCard;